import { Component, OnInit, OnDestroy } from "@angular/core";
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { TranslateService } from "@ngx-translate/core";
import { SessionContextService } from "projects/packcert-web/src/app/context/session-context.service";
import { DataProvider } from 'projects/packcert-web/src/app/providers/data.provider';
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { Message, SpinnerService, UtilsService } from 'shared-components';
import { HistoricoContextService } from "../../context/historico-context.service";
import { HistoricoService } from "../../services/historico.service";
import { Router } from "@angular/router";

@Component({
    selector: 'app-historico-consumos',
    templateUrl: './historico-consumos.component.html'
})
export class HistoricoConsumosComponent implements OnInit, OnDestroy {

    private readonly _DEFAULT_ITEMS_PER_PAGE = 10;

    private _filters;
    public filtersVisible = true;
    public filtersForm: UntypedFormGroup;

    public toCloseSpinner = 0;

    public historicoConsumos;
    public denominaciones;
    public campanyas;
    public etiquetadores;
    public almacenes;
    public provincias;
    public municipios;
    public confecciones;
    public marcas;

    public paisId;

    public required = false;

    public disabledDenominaciones: Subject<boolean> = new Subject();
    public disabledCampanyas: Subject<boolean> = new Subject();
    public disabledEtiquetadores: Subject<boolean> = new Subject();
    public disabledAlmacenes: Subject<boolean> = new Subject();
    public disabledProvincias: Subject<boolean> = new Subject();
    public disabledMunicipios: Subject<boolean> = new Subject();
    public disabledConfecciones: Subject<boolean> = new Subject();
    public disabledMarcas: Subject<boolean> = new Subject();

    public mensajes = new Array<Message>();
    public showError: boolean;
    public showWarning: boolean;

    public dropdownInfo: number;
    public dropdownSection: number;
    public pestanaActiva = new Array<number>();

    public itemsPerPage = this._DEFAULT_ITEMS_PER_PAGE;
    public currentPage = 0;
    public totalItems: number;
    public readonly visiblePages = 3;

    public orderBy: string;
    public columnId: number;

    private readonly destroy$ = new Subject<void>();

    constructor(
        private readonly _router: Router,
        private _fb: UntypedFormBuilder,
        private readonly _translate: TranslateService,
        private readonly _sessionContext: SessionContextService,
        private readonly _spinnerService: SpinnerService,
        private readonly _historicoService: HistoricoService,
        private readonly _historicoContext: HistoricoContextService,
        private readonly _dataProvider: DataProvider,
        private readonly _utilsService: UtilsService
    ) { }

    ngOnInit() {
        this.filtersForm = this._fb.group({
            pais: ['', Validators.required],
            denominacion: [{ value: '', disabled: true }, Validators.required],
            campanya: [{ value: '', disabled: true }, Validators.required],
            fechaInicio: [''],
            fechaFin: [''],
            etiquetador: [{ value: '', disabled: true }, Validators.required],
            almacen: [{ value: '', disabled: true }],
            provincia: [{ value: '', disabled: true }],
            municipio: [{ value: '', disabled: true }],
            confeccion: [{ value: '', disabled: true }],
            marca: [{ value: '', disabled: true }],
            destino: [''],
            snc: [''],
            desProductor: [''],
        });

        this._historicoContext.historicoConsumosFilters = null;

        this.checkWarnings(this.filtersForm.get('campanya').value);

        this.filtersForm.get('fechaInicio').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.validDates());
        this.filtersForm.get('fechaFin').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.validDates());

        this.filtersForm.get('pais').valueChanges.pipe(takeUntil(this.destroy$)).subscribe((pais) => {
            if (pais) {
                this.paisId = pais.id;
                this.getDenominacionesByPais(pais.id);
                this.getProvinciasByPais(pais.id);
            } else {
                this.disabledDenominaciones.next(true);
                this.disabledProvincias.next(true);
                this.filtersForm.get('denominacion').patchValue('');
                this.filtersForm.get('denominacion').disable();
                this.filtersForm.get('provincia').patchValue('');
                this.filtersForm.get('provincia').disable();
                this.denominaciones = [];
                this.provincias = [];
            }
        });

        if (this._dataProvider.paises) {
            if (this._dataProvider.paises.length === 1 && this.filtersForm.get('pais').enabled) {
                this.filtersForm.get('pais').patchValue(this._dataProvider.paises[0]);
                this.filtersForm.get('pais').disable();
            }
        }

        this.filtersForm.get('provincia').valueChanges.pipe(takeUntil(this.destroy$)).subscribe((provincia) => {
            if (provincia) {
                this.filtersForm.get('municipio').setValidators(Validators.required);
                this.required = true;
                this.getMunicipiosByProvincia(provincia.id, this.paisId);
            } else {
                this.disabledMunicipios.next(true);
                this.filtersForm.get('municipio').patchValue('');
                this.filtersForm.get('municipio').disable();
                this.filtersForm.get('municipio').clearValidators();
                this.required = false;
                this.municipios = [];
            }
        });

        this.filtersForm.get('denominacion').valueChanges.pipe(takeUntil(this.destroy$)).subscribe((denominacion) => {
            if (denominacion) {
                this.getCampanyasByDenominacion(denominacion.id, this.paisId);
            } else {
                this.disabledCampanyas.next(true);
                this.filtersForm.get('campanya').patchValue('');
                this.filtersForm.get('campanya').disable();
                this.campanyas = [];
            }
        });

        this.filtersForm.get('campanya').valueChanges.pipe(takeUntil(this.destroy$)).subscribe((campanya) => {
            if (campanya) {
                this.getEtiquetadoresByCampanya(campanya.id, this.paisId);
            } else {
                this.disabledEtiquetadores.next(true);
                this.filtersForm.get('etiquetador').patchValue('');
                this.filtersForm.get('etiquetador').disable();
                this.etiquetadores = [];
            }
        });

        this.filtersForm.get('etiquetador').valueChanges.pipe(takeUntil(this.destroy$)).subscribe((etiquetador) => {
            if (etiquetador) {
                this.getMarcasByEtiquetador(etiquetador.id, this.paisId);
                this.getConfeccionesByEtiquetador(etiquetador.id, this.paisId);
                this.getAlmacenesByEtiquetador(etiquetador.id, this.paisId);
            } else {
                this.disabledMarcas.next(true);
                this.disabledConfecciones.next(true);
                this.disabledAlmacenes.next(true);
                this.filtersForm.get('marca').patchValue('');
                this.filtersForm.get('confeccion').patchValue('');
                this.filtersForm.get('almacen').patchValue('');
                this.filtersForm.get('marca').disable();
                this.filtersForm.get('confeccion').disable();
                this.filtersForm.get('almacen').disable();
                this.marcas = [];
                this.confecciones = [];
                this.almacenes = [];
            }
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public get defaultLanguage() {
        return this._translate.getDefaultLang();
    }

    public get role() {
        return this._sessionContext.sessionUser.role;
    }

    public get paises() {
        return this._dataProvider.paises;
    }

    public get destinos() {
        return this._dataProvider.destinos;
    }

    public get isSmallScreen() {
        return this._sessionContext.isSmallScreen();
    }

    public getDenominacionesByPais(paisId) {
        let filters = {
            paisId: paisId
        };
        this._spinnerService.showSpinner();
        this.toCloseSpinner += 1;
        this._historicoService.getDenominaciones(filters).toPromise().finally(() => {
            this.checkCloseSpinner();
        }).then((resp) => {
            if (resp.lista.length > 0) {
                this.denominaciones = resp.lista;
                if (this.denominaciones.length === 1) {
                    this.filtersForm.get('denominacion').patchValue(this.denominaciones[0]);
                    this.filtersForm.get('denominacion').disable();
                    this.disabledDenominaciones.next(true);
                } else {
                    this.filtersForm.get('denominacion').enable();
                    this.disabledDenominaciones.next(false);
                }
            }
        });
    }

    public getCampanyasByDenominacion(denominacionId, paisId) {
        let filters = {
            denominacionId: denominacionId,
            paisId: paisId,
            volcadoHistorico: true
        };
        this._spinnerService.showSpinner();
        this.toCloseSpinner += 1;
        this._historicoService.getCampanyas(filters).toPromise().finally(() => {
            this.checkCloseSpinner();
        }).then((resp) => {
            if (resp.lista.length > 0) {
                this.campanyas = resp.lista;
                this.campanyas.forEach(campanya => {
                    campanya.des = campanya.anyo.toString();
                });
                if (this.campanyas.length === 1) {
                    this.filtersForm.get('campanya').patchValue(this.campanyas[0]);
                } else {
                    this.filtersForm.get('campanya').enable();
                    this.disabledCampanyas.next(false);
                }
            }
        });
    }

    public getProvinciasByPais(paisId) {
        let filters = {
            paisId: paisId
        };
        this._spinnerService.showSpinner();
        this.toCloseSpinner += 1;
        this._historicoService.getProvincias(filters).toPromise().finally(() => {
            this.checkCloseSpinner();
        }).then((resp) => {
            if (resp.lista.length > 0) {
                this.provincias = resp.lista;
                this.filtersForm.get('provincia').enable();
                this.disabledProvincias.next(false);
            }
        });
    }

    public getMunicipiosByProvincia(provinciaId, paisId) {
        let filters = {
            paisId: paisId,
            provinciaId: provinciaId
        };
        this._spinnerService.showSpinner();
        this.toCloseSpinner += 1;
        this._historicoService.getMunicipios(filters).toPromise().finally(() => {
            this.checkCloseSpinner();
        }).then((resp) => {
            if (resp.lista.length > 0) {
                this.municipios = resp.lista;
                this.filtersForm.get('municipio').enable();
                this.disabledMunicipios.next(false);
            }
        });
    }

    public getEtiquetadoresByCampanya(campanyaId, paisId) {
        let filters = {
            paisId: paisId,
            campanyaId: campanyaId
        };
        this._spinnerService.showSpinner();
        this.toCloseSpinner += 1;
        this._historicoService.getEtiquetadores(filters).toPromise().finally(() => {
            this.checkCloseSpinner();
        }).then((resp) => {
            if (resp.lista.length > 0) {
                this.etiquetadores = resp.lista;
                if (this.etiquetadores.length === 1) {
                    this.filtersForm.get('etiquetador').patchValue(this.etiquetadores[0]);
                    this.filtersForm.get('etiquetador').disable();
                    this.disabledEtiquetadores.next(true);
                } else {
                    this.filtersForm.get('etiquetador').enable();
                    this.disabledEtiquetadores.next(false);
                }

            }
        });
    }

    public getConfeccionesByEtiquetador(etiquetadorId, paisId) {
        let filters = {
            paisId: paisId,
            etiquetadorId: etiquetadorId
        };
        this._spinnerService.showSpinner();
        this.toCloseSpinner += 1;
        this._historicoService.getConfecciones(filters).toPromise().finally(() => {
            this.checkCloseSpinner();
        }).then((resp) => {
            if (resp.lista.length > 0) {
                this.confecciones = resp.lista;
                this.filtersForm.get('confeccion').enable();
                this.disabledConfecciones.next(false);
            }
        });
    }

    public getAlmacenesByEtiquetador(etiquetadorId, paisId) {
        let filters = {
            paisId: paisId,
            etiquetadorId: etiquetadorId
        };
        this._spinnerService.showSpinner();
        this.toCloseSpinner += 1;
        this._historicoService.getAlmacenes(filters).toPromise().finally(() => {
            this.checkCloseSpinner();
        }).then((resp) => {
            if (resp.lista.length > 0) {
                this.almacenes = resp.lista;
                this.filtersForm.get('almacen').enable();
                this.disabledAlmacenes.next(false);
            }
        });
    }

    public getMarcasByEtiquetador(etiquetadorId, paisId) {
        let filters = {
            paisId: paisId,
            etiquetadorId: etiquetadorId
        };
        this._spinnerService.showSpinner();
        this.toCloseSpinner += 1;
        this._historicoService.getMarcas(filters).toPromise().finally(() => {
            this.checkCloseSpinner();
        }).then((resp) => {
            if (resp.lista.length > 0) {
                this.marcas = resp.lista;
                this.filtersForm.get('marca').enable();
                this.disabledMarcas.next(false);
            }
        });
    }

    private checkCloseSpinner() {
        this.toCloseSpinner -= 1;
        if (this.toCloseSpinner === 0) {
            this._spinnerService.hideSpinner();
        }
    }

    public validDates() {
        if (this.filtersForm.get('fechaInicio').value && this.filtersForm.get('fechaFin').value) {
            if (this.checkDates(this.filtersForm.get('fechaInicio').value, this.filtersForm.get('fechaFin').value)) {
                this.clearDatesValidators();
            } else {
                this.filtersForm.get('fechaInicio').setErrors(['Invalid date']);
                this.filtersForm.get('fechaFin').setErrors(['Invalid date']);
            }
            if (this.filtersForm.get('fechaInicio').invalid || this.filtersForm.get('fechaFin').invalid) {
                this.showError = true;
                this.mensajes.push({ class: 'error', message: 'global.fecha.error.finAnteriorInicio' });
            } else {
                this.showError = false;
                this.mensajes = [];
            }
        } else {
            this.clearDatesValidators();
            this.showError = false;
            this.mensajes = [];
        }
    }

    private clearDatesValidators() {
        if (this.filtersForm.get('fechaFin').errors && this.filtersForm.get('fechaFin').errors.includes('Invalid date')) {
            this.filtersForm.get('fechaFin').clearValidators();
            this.filtersForm.get('fechaFin').updateValueAndValidity();
        }
        if (this.filtersForm.get('fechaInicio').errors && this.filtersForm.get('fechaInicio').errors.includes('Invalid date')) {
            this.filtersForm.get('fechaInicio').clearValidators();
            this.filtersForm.get('fechaInicio').updateValueAndValidity();
        }
    }

    private checkDates(inicio, fin) {
        const inicioD = new Date(inicio);
        const finD = new Date(fin);
        return inicioD <= finD;
    }

    public pageChange(page) {
        this.dropdownInfo = -1;
        this._historicoContext.historicoConsumosFilters.numPagina = this.currentPage = page - 1;
        this.getHistoricoConsumos();
    }

    public itemsPerPageChange(items) {
        this.dropdownInfo = -1;
        this._historicoContext.historicoConsumosFilters.numPagina = this.currentPage = 0;
        this._historicoContext.historicoConsumosFilters.tamPagina = this.itemsPerPage = items;
        this.getHistoricoConsumos();
    }

    private checkWarnings(campanya) {
        if (campanya && this.filtersForm.valid) {
            this.filtersVisible = false;
            this.showWarning = false;
            this.mensajes = [];
            this.getHistoricoConsumos();
        } else {
            this.filtersVisible = true;
            this.showWarning = true;
            this.mensajes.push({ class: 'warning', message: 'global.info.filterRequired' });
        }
    }

    public checkValuesForm(): boolean {
        return (
            (this.filtersForm.get('pais').value && this.filtersForm.get('pais').enabled) ||
            (this.filtersForm.get('denominacion').value && this.filtersForm.get('denominacion').enabled) ||
            this.filtersForm.get('campanya').value ||
            this.filtersForm.get('provincia').value ||
            this.filtersForm.get('municipio').value ||
            this.filtersForm.get('etiquetador').value ||
            this.filtersForm.get('marca').value ||
            this.filtersForm.get('confeccion').value ||
            this.filtersForm.get('almacen').value ||
            this.filtersForm.get('desProductor').value ||
            this.filtersForm.get('snc').value ||
            this.filtersForm.get('destino').value ||
            this.filtersForm.get('fechaInicio').value ||
            this.filtersForm.get('fechaFin').value) ? true : false;
    }

    public resetForm() {
      if(this.filtersForm.get('pais').disabled){
        this.filtersForm.reset({
          pais: this.filtersForm.get('pais').value
        });
      } else {
        this.filtersForm.controls['pais'].reset();
        this.filtersForm.controls['destino'].reset();
        this.filtersForm.controls['desProductor'].reset();
        this.filtersForm.controls['snc'].reset();
        this.filtersForm.controls['fechaInicio'].reset();
        this.filtersForm.controls['fechaFin'].reset();
      }
    }

    public showDetails(i: number) {
        this.dropdownInfo = this.dropdownInfo === i ? -1 : i;
        // Desplazamiento a la izquierda para tablet (mismo tamaño máximo que en table.scss)
        if (this.dropdownInfo !== -1 && window.innerWidth <= 1199) {
            // Retardo en caso de que haya otro detalle abierto
            setTimeout(() => {
                document.querySelector('table')?.scrollIntoView({behavior: 'smooth', block: 'nearest', inline: 'start'});
            }, 50);
        }
    }

    public filtrar() {
        this.validDates();
        if (this.filtersForm.invalid) {
            if (Object.keys(this.filtersForm.controls).some(key => this.filtersForm.get(key).hasError('required'))) {
                this.showError = true;
                this.mensajes.push({ class: 'error', message: 'global.error.required' });
            }
        } else {
            this.applyFilters();
        }
    }

    private applyFilters() {
        this.currentPage = 0;
        this._historicoContext.historicoConsumosFilters = {
            campanyaId: this.filtersForm.get('campanya').value ? this.filtersForm.get('campanya').value.id : null,
            etiquetadorId: this.filtersForm.get('etiquetador').value ? this.filtersForm.get('etiquetador').value.id : null,
            marcaId: this.filtersForm.get('marca').value ? this.filtersForm.get('marca').value.id : null,
            confeccionId: this.filtersForm.get('confeccion').value ? this.filtersForm.get('confeccion').value.id : null,
            almacenId: this.filtersForm.get('almacen').value ? this.filtersForm.get('almacen').value.id : null,
            paisDestinoId: this.filtersForm.get('destino').value ? this.filtersForm.get('destino').value.id : null,
            municipioId: this.filtersForm.get('municipio').value ? this.filtersForm.get('municipio').value.id : null,
            snc: this.filtersForm.get('snc').value ? this.filtersForm.get('snc').value : '',
            desProductor: this.filtersForm.get('desProductor').value ? this.filtersForm.get('desProductor').value : '',
            fechaInicio: this.filtersForm.get('fechaInicio').value ?
                this._utilsService.parseGMTtoYYYYMMDD(this.filtersForm.get('fechaInicio').value) : '',
            fechaFin: this.filtersForm.get('fechaFin').value ?
                this._utilsService.parseGMTtoYYYYMMDD(this.filtersForm.get('fechaFin').value) : '',
            numPagina: this.currentPage,
            tamPagina: this.itemsPerPage
        };
        this.getHistoricoConsumos();
    }

    public getHistoricoConsumos() {
        if (this._historicoContext.historicoConsumosFilters) {
            this._filters = this._historicoContext.historicoConsumosFilters
        } else {
            this.applyFilters();
        }
        this._spinnerService.showSpinner();
        this._historicoService.getHistoricoConsumos(this._filters).toPromise().finally(() => {
            this._spinnerService.hideSpinner();
        }).then((resp) => {
            this.totalItems = resp.paginacionMeta.totalCount;
            this.historicoConsumos = resp.lista;
        }, (error) => {
            this.showError = true;
            this.mensajes.push({ class: 'error', message: 'global.error.noResponse' });
        });
    }

    public getRolePermission() {
        let disabled = false;

        if (this.role === 'Administrador') {
            disabled = false;
        } else if (this.role === 'Auditor') {
            disabled = false;
        } else if (this.role === 'Supervisor') {
            disabled = false;
        }

        return disabled;
    }

    public getIcon(prop: string) {
        let iconClass = 'fa fa-sort';
        if (this._filters && this._filters.orderItems && this._filters.orderItems[0].orderProperty === prop) {
            iconClass = this._filters.orderItems[0].orderByDescending === true ? 'fa fa-sort-down' : 'fa fa-sort-up';
        }
        return iconClass;
    }

    public toOrderBy(columnFilter, idColumn) {
        this.columnId = idColumn;
        if (this.historicoConsumos && this.historicoConsumos.length > 0) {
            if (this._historicoContext.historicoConsumosFilters) {
                this._filters = this._historicoContext.historicoConsumosFilters;
                if (!this.orderBy || this.orderBy.includes('DESC')) {
                    this.orderBy = 'ASC' + idColumn;
                    this._filters.orderItems = [
                        {
                            orderProperty: columnFilter,
                            orderByDescending: false
                        }
                    ];
                } else if (this.orderBy.includes('ASC')) {
                    this.orderBy = 'DESC' + idColumn;
                    this._filters.orderItems = [
                        {
                            orderProperty: columnFilter,
                            orderByDescending: true
                        }
                    ];
                }
                this.getHistoricoConsumos();
            }
        }
    }

    public openDocHelp() {
      this._spinnerService.showSpinner();
      this.toCloseSpinner += 1;
      this._historicoService.getDocHelp('History_Transactions.pdf').toPromise().finally(() => {
          this.checkCloseSpinner();
      }).then((resp) => {
          localStorage.setItem('base64', resp.base64Archivo);
          const url = this._router.serializeUrl(this._router.createUrlTree(['viewPdf']));
          window.open(url, '_blank');
      });
    }

    public trackByConsumoId(index: number, historico: { consumoId: number }): number {
      return historico.consumoId;
    }
}
